import { FC, useState } from 'react';
import { Box, Container, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { DotStepper } from '../../components/dot-stepper.tsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { Paths } from '../../constants/paths.ts';
import { useStudyQuery } from '../../graphql/generated/operations/study.ts';
import { trackEvent } from '../../utils/analytics.ts';

export const TutorialPage: FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();
  const { state } = useLocation();
  const { data } = useStudyQuery();

  return (
    <Container
      maxWidth="md"
      sx={{
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        pt: 5
      }}
    >
      {activeStep === 0 && (
        <>
          <Typography variant="h4" sx={{ mb: 3 }}>
            Tutorial
          </Typography>
          <Box component="img" src="/questions.svg" sx={{ height: '200px', mb: 3 }} />
          <Typography sx={{ fontSize: '1.125rem' }}>
            First, we will ask you some questions about yourself in order to customize the activity suggestions to your values and goals.
          </Typography>
        </>
      )}
      {activeStep === 1 && (
        <Stack spacing={3} sx={{ mb: 3 }}>
          <Box component="img" src="/activity-types.svg" sx={{ height: '200px', mb: 3 }} />
          <Typography sx={{ fontSize: '1.125rem' }}>
            The ABLE app will then provide you with a list of customized values-based activities and goals, across different areas of your interests. Choose one of these activities
            or create your own!
          </Typography>
          <Typography variant="h6" sx={{ mt: 2 }}>
            Choose activities from the following types:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Challenging or Meaningful activities"
                secondary={
                  'Activities that absorb the mind, require concentration, and promote a sense of accomplishment (for example, engaging in a hobby or learning a skill). You feel that that \u201Ctime flies\u201D when doing this activity.'
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="Pleasant activity" secondary="Activities that provide a short, pleasant break from the pace of life." />
            </ListItem>
            {data?.study.allowSocialActivity && (
              <ListItem>
                <ListItemText primary="Social Connection" secondary="Activities that involve interactions with others." />
              </ListItem>
            )}
          </List>
        </Stack>
      )}
      {activeStep === 2 && (
        <Stack spacing={3}>
          <Box component="img" src="/calendar.svg" sx={{ height: '200px', mb: 3 }} />
          <Typography sx={{ fontSize: '1.125rem' }}>
            Next, schedule times in the upcoming week to do your activity. Each week, you&apos;ll be asked if you want to keep the same activities or change to a new one.
          </Typography>
        </Stack>
      )}
      {activeStep === 3 && (
        <Stack spacing={3}>
          <Box component="img" src="/trophy.svg" sx={{ height: '200px', mb: 3 }} />
          <Typography sx={{ fontSize: '1.125rem' }}>
            The app will prompt you to log your activities after you&apos;ve done them, so it can give you weekly feedback on your mood and stress levels.
          </Typography>
        </Stack>
      )}
      <Box flexGrow={1} />
      <Box sx={{ position: 'fixed', bottom: 0, left: 0, width: '100%' }}>
        <DotStepper
          steps={4}
          activeStep={activeStep}
          onBack={() => {
            const nextStep = Math.max(0, activeStep - 1);
            setActiveStep(nextStep);
            trackEvent('tutorial_back', { step: nextStep });
          }}
          onNext={() => {
            const nextStep = Math.min(activeStep + 1, 4);
            setActiveStep(nextStep);
            trackEvent('tutorial_next', { step: nextStep });
          }}
          onCompleted={() => {
            trackEvent('tutorial_complete');
            if (!state) {
              window.sessionStorage.setItem('post_notification_route', Paths.LET_GET_STARTED);
            }
            if (state) {
              navigate(state);
            } else {
              navigate(Paths.USER_PROFILE);
            }
          }}
        />
      </Box>
    </Container>
  );
};
